import { useState, useEffect, useRef } from 'react';

export interface UseDropdownStateOptions {
  /** Initial open state */
  initialOpen?: boolean;
  /** Controlled open state from parent */
  isOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Whether to handle click-outside to close */
  closeOnClickOutside?: boolean;
  /** Disabled state prevents toggling */
  disabled?: boolean;
}

export interface UseDropdownStateReturn {
  /** Current open state */
  isOpen: boolean;
  /** Set the open state */
  setIsOpen: (isOpen: boolean) => void;
  /** Toggle the open state */
  toggleDropdown: () => void;
  /** Ref for the dropdown container (attach to parent element) */
  dropdownRef: React.RefObject<HTMLDivElement>;
  /** Internal ref to prevent immediate closure on toggle */
  isTogglingRef: React.RefObject<boolean>;
}

/**
 * Hook to manage dropdown state with click-outside detection
 * 
 * Consolidates dropdown open/close logic used across PrimaryTag, SecondaryTag, 
 * TertiaryTag, and InteractiveTag components.
 * 
 * @example
 * ```tsx
 * const { isOpen, toggleDropdown, dropdownRef } = useDropdownState({
 *   closeOnClickOutside: true,
 *   onOpenChange: (open) => console.log('Dropdown is', open)
 * });
 * 
 * return (
 *   <div ref={dropdownRef}>
 *     <button onClick={toggleDropdown}>Toggle</button>
 *     {isOpen && <Dropdown />}
 *   </div>
 * );
 * ```
 */
export function useDropdownState(options: UseDropdownStateOptions = {}): UseDropdownStateReturn {
  const {
    initialOpen = false,
    isOpen: controlledIsOpen,
    onOpenChange,
    closeOnClickOutside = true,
    disabled = false
  } = options;

  // Internal state
  const [internalIsOpen, setInternalIsOpen] = useState(initialOpen);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  
  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isTogglingRef = useRef<boolean>(false);

  // Set open state with callback
  const setIsOpen = (newIsOpen: boolean) => {
    if (disabled) return;
    
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };

  // Toggle function
  const toggleDropdown = () => {
    if (disabled) return;
    
    // Set flag to prevent immediate closure
    isTogglingRef.current = true;
    setIsOpen(!isOpen);
    
    // Reset flag after a delay to ensure toggle completes
    setTimeout(() => {
      isTogglingRef.current = false;
    }, 200);
  };

  // Click outside handler
  useEffect(() => {
    if (!closeOnClickOutside || !isOpen || disabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if we're currently toggling
      if (isTogglingRef.current) {
        return;
      }
      
      const target = event.target as HTMLElement;
      
      // Don't close if clicking inside a portal dropdown (e.g., OptionDropdown)
      if (target.closest('[data-dropdown-portal="true"]')) {
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnClickOutside, disabled]);

  return {
    isOpen,
    setIsOpen,
    toggleDropdown,
    dropdownRef,
    isTogglingRef
  };
}

